import {
    DimensionExportToReport,
    OverviewPreviewReportStructureExport,
} from './../structure/structureOverviewPreviewReport'
import { useCallback, useState } from 'react'
import Excel, { Borders, Fill } from 'exceljs'
import excelUnit from '../../../utils/excel'
import { rewriteRecommendToReport, ModeTableRecommended } from '../components/OverviewTableRecommend'
import { FieldType } from '../../questionnaire/FormStructureQuestionnaireType'

const pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToHeight: 21,
    fitToWidth: 29.7,
    // paperSize: 9,
} as Partial<Excel.PageSetup>

const styleBorder = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
} as Partial<Borders>

const styleFillGray = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {
        argb: 'd9d9d9',
    },
} as Fill

const styleFillYellow = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {
        argb: 'f5d01a',
    },
} as Fill

const styleFillYellowLight = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {
        argb: 'fdf5cb',
    },
} as Fill

const styleFillBlue = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {
        argb: 'd4e8dc',
    },
} as Fill

const styleFillBlueLight = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {
        argb: 'f6fbf7',
    },
} as Fill

type DataTableSummary = {
    // gradeAdmin: string | null | undefined
    // gradeUser: string | null | undefined
    descriptionAdmin: string | null | undefined
    descriptionUser: string | null | undefined
    id: number
    name: string
    scoreAdmin: number | null | undefined
    scoreFull: number
    scoreUser: number | null | undefined
}

const useExportReportOverviewExcel = () => {
    const [isLoadingDownloadExcel, setLoadingDownloadExcel] = useState(false)
    const { createWorkbook, createWorksheet, createFileDownload } = excelUnit

    const addTitle = useCallback(({ worksheet, titleName }: { worksheet: Excel.Worksheet; titleName: string }) => {
        worksheet.addRow([titleName])
        worksheet.getRow(worksheet.rowCount).height = 40
        worksheet.getRow(worksheet.rowCount).alignment = {
            horizontal: 'left',
            vertical: 'middle',
            wrapText: true,
        }
        worksheet.mergeCells(worksheet.rowCount, 1, worksheet.rowCount, 6)
    }, [])

    const addImage = useCallback(
        ({
            workbook,
            worksheet,
            imageBase64,
        }: {
            workbook: Excel.Workbook
            worksheet: Excel.Worksheet
            imageBase64: string
        }) => {
            const image = workbook.addImage({
                base64: imageBase64,
                extension: 'png',
            })

            worksheet.addImage(image, {
                tl: { col: 0, row: 3 },
                // tl: { col: 1.5, row: 3 },
                ext: { width: 560, height: 530 },
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const createTableSummary = useCallback(
        ({
            prefixLabel,
            titleName,
            order,
            worksheet,
            data,
        }: {
            worksheet: Excel.Worksheet
            workbook: Excel.Workbook
            prefixLabel: string
            order: number
            titleName: string
            // sheetName: string
            // image: string
            data: DataTableSummary[]
        }) => {
            let startChartSummaryImageRow = 33 // row เริ่มต้น กราฟรวมทุกด้านของแบบประเมิน
            const columns1 = [titleName, 'คะแนนเต็ม', 'คะแนนโดยชุมชน', '', 'คะแนนโดยเจ้าหน้าที่', '']
            const columns2 = ['', '', 'คะแนนที่ได้', 'ระดับคะแนน', 'คะแนนที่ได้', 'ระดับคะแนน']

            const columns = [columns1, columns2]

            const newDataTableSummary = data.map((item, index) => ({
                ...item,
                name: `${prefixLabel}${index + 1} ${item.name}`,
            }))

            //  ****  add header table *****
            worksheet.insertRows(startChartSummaryImageRow, columns)

            //เป้าประสงค์
            worksheet.getCell(worksheet.rowCount - 1, 1).fill = styleFillGray

            //คะแนนเต็ม
            worksheet.getCell(worksheet.rowCount - 1, 2).fill = styleFillGray
            // ****  คะแนนโดยชุมชน  ******
            worksheet.getCell(worksheet.rowCount - 1, 3).fill = styleFillYellow

            // คะแนนที่ได้
            worksheet.getCell(worksheet.rowCount, 3).fill = styleFillYellow
            // ระดับคะแนน
            worksheet.getCell(worksheet.rowCount, 4).fill = styleFillYellow

            // ***** คะแนนโดยเจ้าหน้าที่ *******
            worksheet.getCell(worksheet.rowCount - 1, 5).fill = styleFillBlue

            // คะแนนที่ได้
            worksheet.getCell(worksheet.rowCount, 5).fill = styleFillBlue
            // ระดับคะแนน
            worksheet.getCell(worksheet.rowCount, 6).fill = styleFillBlue

            columns1.forEach((col, index) => {
                worksheet.getCell(worksheet.rowCount - 1, index + 1).alignment = {
                    horizontal: 'center',
                    vertical: 'middle',
                }

                worksheet.getCell(worksheet.rowCount - 1, index + 1).border = styleBorder

                worksheet.getCell(worksheet.rowCount, index + 1).alignment = {
                    horizontal: 'center',
                    vertical: 'middle',
                }

                worksheet.getCell(worksheet.rowCount, index + 1).border = styleBorder

                worksheet.getRow(worksheet.rowCount - 1).height = 40
                worksheet.getRow(worksheet.rowCount).height = 40
            })

            // เป้าประสงค์
            // merge by start row, start column, end row, end column
            worksheet.mergeCells(worksheet.rowCount - 1, 1, worksheet.rowCount, 1)

            // คะแนนเต็ม
            // merge by start row, start column, end row, end column
            worksheet.mergeCells(worksheet.rowCount - 1, 2, worksheet.rowCount, 2)

            // คะแนนโดยชุมชน
            // merge by start row, start column, end row, end column
            worksheet.mergeCells(worksheet.rowCount - 1, 3, worksheet.rowCount - 1, 4)

            // คะแนนโดยเจ้าหน้าที่
            // merge by start row, start column, end row, end column
            worksheet.mergeCells(worksheet.rowCount - 1, 5, worksheet.rowCount - 1, 6)

            //  ****  add header table *****

            //  ****  add header columns *****
            worksheet.columns = [
                {
                    key: 'name',
                    width: 70,
                    alignment: {
                        wrapText: true,
                        horizontal: 'center',
                        shrinkToFit: true,
                    },
                    fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: {
                            argb: 'd9d9d9',
                        },
                    },
                },
                {
                    key: 'scoreFull',
                    width: 20,
                    alignment: {
                        horizontal: 'center',
                        shrinkToFit: true,
                    },
                    fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: {
                            argb: 'd9d9d9',
                        },
                    },
                },
                { key: 'scoreUser', width: 30 },
                { key: 'descriptionUser', width: 30 },
                { key: 'scoreAdmin', width: 30 },
                { key: 'descriptionAdmin', width: 30 },
            ] as Excel.Worksheet['columns']

            //  ****  add header columns *****

            const isScoreAdminUndefined = data.every((item) => typeof item.scoreAdmin === 'undefined')
            const isScoreUserUndefined = data.every((item) => typeof item.scoreUser === 'undefined')

            const isScoreAdminNull = data.every((item) => item.scoreAdmin === null)
            const isScoreUserNull = data.every((item) => item.scoreUser === null)

            let totalScoreFull = 0
            let isNumberScoreFull = true
            const arrayTotalScoreFull = data.map((item) => item.scoreFull).filter((item) => typeof item === 'number')
            if (arrayTotalScoreFull.length > 0) {
                totalScoreFull = arrayTotalScoreFull.reduce((acc, crr) => {
                    return (acc as number) + (crr as number)
                })
            } else {
                isNumberScoreFull = false
            }

            let totalScoreUser: number | null | undefined = 0
            let totalScoreAdmin: number | null | undefined = 0

            const arrayScoreUser = data.map((item) => item.scoreUser).filter((item) => typeof item === 'number')
            if (arrayScoreUser.length > 0) {
                totalScoreUser = arrayScoreUser.reduce((acc, crr) => {
                    return (acc as number) + (crr as number)
                })
            }

            const arrayScoreAdmin = data.map((item) => item.scoreAdmin).filter((item) => typeof item === 'number')
            if (arrayScoreAdmin.length > 0) {
                totalScoreAdmin = arrayScoreAdmin.reduce((acc, crr) => {
                    return (acc as number) + (crr as number)
                })
            }

            let percentScoreUser
            let percentScoreAdmin

            if (typeof totalScoreUser === 'number') {
                percentScoreUser = (totalScoreUser * 100) / totalScoreFull
            }

            if (typeof totalScoreAdmin === 'number') {
                percentScoreAdmin = (totalScoreAdmin * 100) / totalScoreFull
            }

            worksheet.insertRows(startChartSummaryImageRow + 2, newDataTableSummary) // บวกตำแหน่งหัวตาราง 2 row

            // *** จัด style table rows
            newDataTableSummary.forEach((row, index) => {
                worksheet.getRow(worksheet.rowCount - index).height = 40

                columns2.forEach((col, indexCol) => {
                    worksheet.getCell(worksheet.rowCount - index, indexCol + 1).alignment = {
                        horizontal: indexCol > 0 ? 'center' : 'left',
                        vertical: 'middle',
                        wrapText: true,
                    }

                    worksheet.getCell(worksheet.rowCount - index, indexCol + 1).border = styleBorder

                    if (indexCol === 2 || indexCol === 3) {
                        //คะแนนโดยชุมชน
                        //คะแนนที่ได้ 2
                        //ระดับคะแนน 3
                        let cellUser = worksheet.getCell(worksheet.rowCount - index, indexCol + 1)
                        cellUser.value = cellUser.value || '-'
                        if (typeof cellUser.value === 'number') cellUser.numFmt = '0.00'
                        cellUser.fill = styleFillYellowLight
                    }

                    if (indexCol === 4 || indexCol === 5) {
                        //คะแนนโดยเจ้าหน้าที่
                        //คะแนนที่ได้ 4
                        //ระดับคะแนน 5
                        let cellAdmin = worksheet.getCell(worksheet.rowCount - index, indexCol + 1)
                        cellAdmin.value = cellAdmin.value || '-'
                        if (typeof cellAdmin.value === 'number') cellAdmin.numFmt = '0.00'
                        cellAdmin.fill = styleFillBlueLight
                    }
                })
            })

            // *** จัด style table rows

            worksheet.insertRows(worksheet.rowCount + 1, [
                {
                    name: 'รวม',
                    scoreFull:
                        isNumberScoreFull && (!isScoreAdminUndefined || !isScoreUserUndefined) ? totalScoreFull : '-',
                    scoreUser: !isScoreUserUndefined && !isScoreUserNull ? totalScoreUser?.toFixed(2) : '-',
                    descriptionUser:
                        typeof percentScoreUser === 'number' && !isScoreUserUndefined && !isScoreUserNull
                            ? `คิดเป็น ${percentScoreUser.toFixed(2)}%`
                            : '-',
                    scoreAdmin: totalScoreAdmin ? totalScoreAdmin?.toFixed(2) : '-',
                    descriptionAdmin:
                        typeof percentScoreAdmin === 'number' && !isScoreAdminUndefined && !isScoreAdminNull
                            ? `คิดเป็น ${percentScoreAdmin.toFixed(2)}%`
                            : '-',
                },
            ]) // บวก row ตัวเองไป 1

            // **** ผมรวม *****
            columns2.forEach((col, index) => {
                //  alignment
                worksheet.getCell(worksheet.rowCount, index + 1).alignment = {
                    horizontal: 'center',
                    vertical: 'middle',
                    shrinkToFit: true,
                }
                worksheet.getCell(worksheet.rowCount, index + 1).border = styleBorder

                worksheet.getRow(worksheet.rowCount).height = 40
            })

            // ** sum คะแนนที่ได้
            worksheet.getCell(worksheet.rowCount, 3).fill = styleFillYellowLight

            // ** sum ระดับคะแนน
            worksheet.getCell(worksheet.rowCount, 4).fill = styleFillYellow

            worksheet.getCell(worksheet.rowCount, 5).fill = styleFillBlueLight

            // ** sum ระดับคะแนน
            worksheet.getCell(worksheet.rowCount, 6).fill = styleFillBlue
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const createTableRecommendQuestions = useCallback(
        ({
            worksheet,
            data,
            order,
            title,
            mode = 'recommend',
        }: {
            data: DimensionExportToReport
            worksheet: Excel.Worksheet
            order: number
            title: string
            mode?: ModeTableRecommended
        }) => {
            const columns = ['ด้านที่ 1 ด้านการบริหารจัดการการท่องเที่ยวโดยชุมชน', 'คำแนะนำโดยละเอียด']

            const titleNumberRow = worksheet.rowCount + 2

            const newDataTableSummary = rewriteRecommendToReport({
                //@ts-ignore
                dataSource: data.purposes,
                mode,
                prefixLabel: `เป้าประสงค์ที่ ${order}.`,
            })

            // *** หัวข้อ   ***
            worksheet.insertRow(titleNumberRow, [title])
            // *** หัวข้อ   ***

            // ***   เพิ่มหัวตาราง     ***
            const columnNumberRow = worksheet.rowCount + 2
            worksheet.insertRow(columnNumberRow, columns)
            worksheet.getRow(worksheet.rowCount).height = 40
            worksheet.getRow(worksheet.rowCount).alignment = {
                vertical: 'middle',
            }

            worksheet.getCell(worksheet.rowCount, 1).border = styleBorder
            worksheet.getCell(worksheet.rowCount, 2).border = styleBorder

            worksheet.getCell(worksheet.rowCount, 1).fill = styleFillGray // เป้าประสงค์ || ด้าน

            worksheet.getCell(worksheet.rowCount, 2).fill = styleFillGray // คะแนนเต็ม

            worksheet.mergeCells(worksheet.rowCount, 2, worksheet.rowCount, 6)

            // ***   เพิ่มหัวตาราง      ***

            // ***   เพิ่มแถวตาราง      ***

            if (newDataTableSummary.length) {
                newDataTableSummary.forEach(({ questions, ...purpose }, purposeIndex: number) => {
                    let RowContAuto = 0
                    if (mode === 'recommend') {
                        questions.forEach(({ autoRecommended = [], ...question }, questionIndex: number) => {
                            worksheet.addRows([[`${purpose.name}`, ` ${question.name} \r\n  - ${question.value}`]])
                            worksheet.getCell(worksheet.rowCount, 1).alignment = {
                                vertical: 'top',
                                horizontal: 'left',
                                wrapText: true,
                            }
                            worksheet.getCell(worksheet.rowCount, 2).alignment = {
                                vertical: 'top',
                                horizontal: 'left',
                                wrapText: true,
                            }

                            worksheet.getCell(worksheet.rowCount, 1).border = styleBorder

                            worksheet.getCell(worksheet.rowCount, 2).border = styleBorder

                            worksheet.getRow(worksheet.rowCount).height = 40
                            worksheet.mergeCells(worksheet.rowCount, 2, worksheet.rowCount, 6)
                        })

                        worksheet.mergeCells(worksheet.rowCount - (questions.length - 1), 1, worksheet.rowCount, 1)
                    } else {
                        //@ts-ignore
                        questions.forEach(({ autoRecommended = [], fieldType, ...question }, questionIndex: number) => {
                            if (fieldType === FieldType.CHECKBOX) {
                                /** หัวข้อการประเมิน */
                                worksheet.addRow([purpose.name, question.name])
                                worksheet.getCell(worksheet.rowCount, 1).border = styleBorder
                                worksheet.getCell(worksheet.rowCount, 2).border = styleBorder
                                worksheet.getRow(worksheet.rowCount).alignment = {
                                    vertical: 'top',
                                    wrapText: true,
                                }
                                worksheet.getRow(worksheet.rowCount).height = 40
                                worksheet.mergeCells(worksheet.rowCount, 2, worksheet.rowCount, 6)

                                /** หัวข้อการประเมิน */

                                autoRecommended.forEach((autoRec) => {
                                    RowContAuto = RowContAuto + 1

                                    worksheet.addRow([
                                        `${purpose.name}`,
                                        `     ตัวเลือกที่ ${autoRec.answerOrder}. ${autoRec.answerName} \r\n          - ${autoRec.answerSuggestion}`,
                                    ])
                                    worksheet.getCell(worksheet.rowCount, 1).border = styleBorder
                                    worksheet.getCell(worksheet.rowCount, 2).border = styleBorder
                                    worksheet.getRow(worksheet.rowCount).alignment = {
                                        vertical: 'top',
                                        wrapText: true,
                                    }
                                    worksheet.getRow(worksheet.rowCount).height = 40
                                    worksheet.mergeCells(worksheet.rowCount, 2, worksheet.rowCount, 6)
                                })
                            } else {
                                // FieldType.CHECKBOX_GROUP
                                /** หัวข้อการประเมิน */
                                worksheet.addRow([purpose.name, question.name])
                                worksheet.getCell(worksheet.rowCount, 1).border = styleBorder
                                worksheet.getCell(worksheet.rowCount, 2).border = styleBorder
                                worksheet.getRow(worksheet.rowCount).alignment = {
                                    vertical: 'top',
                                    wrapText: true,
                                }
                                worksheet.getRow(worksheet.rowCount).height = 40
                                worksheet.mergeCells(worksheet.rowCount, 2, worksheet.rowCount, 6)

                                /** หัวข้อการประเมิน */

                                autoRecommended.forEach(({ subAnswer = [], ...autoRec }, autoRecIndex) => {
                                    RowContAuto = RowContAuto + 1

                                    /**  หัวข้อใหญ่*/
                                    worksheet.addRow([
                                        `${purpose.name}`,
                                        `     หัวข้อใหญ่ที่ ${autoRec.subQuestionID}. ${autoRec.subQuestionName} \r\n`,
                                    ])

                                    worksheet.getCell(worksheet.rowCount, 2).border = styleBorder
                                    worksheet.getRow(worksheet.rowCount).alignment = {
                                        vertical: 'top',
                                        wrapText: true,
                                    }
                                    worksheet.getRow(worksheet.rowCount).height = 40

                                    worksheet.mergeCells(worksheet.rowCount, 2, worksheet.rowCount, 6)

                                    /**  หัวข้อใหญ่*/

                                    /** ตัวเลือก */

                                    subAnswer.forEach((sub) => {
                                        RowContAuto = RowContAuto + 1

                                        worksheet.addRow([
                                            `${purpose.name}`,
                                            `           ตัวเลือกที่ ${sub.answerOrder}. ${sub.answerName} \r\n                     - ${sub.answerSuggestion}`,
                                        ])
                                        worksheet.getCell(worksheet.rowCount, 1).border = styleBorder
                                        worksheet.getCell(worksheet.rowCount, 2).border = styleBorder
                                        worksheet.getRow(worksheet.rowCount).height = 40
                                        worksheet.getRow(worksheet.rowCount).alignment = {
                                            vertical: 'top',
                                            wrapText: true,
                                        }
                                        worksheet.mergeCells(worksheet.rowCount, 2, worksheet.rowCount, 6)
                                    })

                                    /** ตัวเลือก */
                                })
                            }
                        })
                        worksheet.mergeCells(
                            worksheet.rowCount - (questions.length + RowContAuto - 1),
                            1,
                            worksheet.rowCount,
                            1,
                        )
                    }
                })
            } else {
                //ไม่มีข้อแนะนำ
                worksheet.insertRow(worksheet.rowCount + 1, ['ไม่มีข้อมูล', ''])
                worksheet.getRow(worksheet.rowCount).alignment = {
                    horizontal: 'center',
                    vertical: 'middle',
                }
                worksheet.getRow(worksheet.rowCount).height = 40
                worksheet.mergeCells(worksheet.rowCount, 1, worksheet.rowCount, 6)

                columns.forEach((col, index) => {
                    worksheet.getCell(worksheet.rowCount, index + 1).border = styleBorder
                })
            }

            // ***   เพิ่มแถวตาราง      ***
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const createWorksheetDimension = useCallback(
        ({ data, workbook, order }: { data: DimensionExportToReport; workbook: Excel.Workbook; order: number }) => {
            // *** ด้านแบบประเมิน  ***

            const worksheet = createWorksheet({
                workbook,
                sheetName: `ด้านที่ ${order}`,
                options: {
                    pageSetup: pageSetup,
                },
            })

            // **** ชื่อด้าน *****
            addTitle({
                titleName: `ด้านที่ ${order} ${data.name}`,
                worksheet,
            })

            // **** ชื่อแบบประเมิน *****

            // **** กราฟเป้าประสงค์ *****
            addImage({
                workbook,
                worksheet,
                imageBase64: data.dimensionRef as string,
            })

            // **** กราฟเป้าประสงค์ *****
            //  ****  add header table *****

            createTableSummary({
                titleName: 'เป้าประสงค์',
                order,
                prefixLabel: `เป้าประสงค์ที่ ${order}.`,
                workbook,
                worksheet,
                data: data.purposes as DataTableSummary[],
            })

            // **** ตารางคำแนะนำรายข้อคำถาม ****

            createTableRecommendQuestions({
                worksheet,
                order,
                data,
                title: 'คำแนะนำ',
                mode: 'auto-recommend',
            })

            createTableRecommendQuestions({
                worksheet,
                data,
                order,
                title: 'หมายเหตุและข้อคิดเห็นข้อการประเมิน',
                mode: 'recommend',
            })

            // **** ตารางคำแนะนำรายข้อคำถาม ****
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const createWorksheetSummary = useCallback(
        ({
            data,
            workbook,
            order,
        }: {
            data: OverviewPreviewReportStructureExport
            workbook: Excel.Workbook
            order: number
        }) => {
            const worksheet = createWorksheet({
                workbook,
                sheetName: 'สรุปคะแนนรวม ',
                options: {
                    pageSetup: pageSetup,
                },
            })

            // **** ชื่อแบบประเมิน *****
            addTitle({
                worksheet,
                titleName: data.questionnaireName,
            })

            // **** ชื่อแบบประเมิน *****

            // **** กราฟรวมทุกด้านของแบบประเมิน *****

            addImage({
                workbook,
                worksheet,
                imageBase64: data.questionnaireRef as string,
            })

            //  ****  add header table *****

            createTableSummary({
                titleName: 'ด้าน',
                order,
                prefixLabel: 'ด้านที่ ',
                workbook,
                worksheet,
                data: data.dimensions as DataTableSummary[],
            })

            //  ****  add header table *****
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const downloadExcel = ({ data }: { data: OverviewPreviewReportStructureExport[] }) => {
        setLoadingDownloadExcel(true)
        const workbook = createWorkbook()

        //  วนสร้าง sheet
        data.forEach((questionnaire, index: number) => {
            // *** สร้าง  Worksheet สรุปคะแนนรวม ****
            createWorksheetSummary({
                data: questionnaire,
                workbook,
                order: index + 1,
            })

            // *** สร้าง  Worksheet ตามด้าน ****

            questionnaire.dimensions.forEach((dimension, index: number) => {
                createWorksheetDimension({
                    workbook,
                    data: dimension,
                    order: index + 1,
                })
            })
        })

        createFileDownload({
            workbook,
            fileName: 'รายงานสรุปภาพรวมของแบบประเมิน',
        }).then(() => {
            setLoadingDownloadExcel(false)
        })
    }

    return { downloadExcel, isLoadingDownloadExcel }
}

export default useExportReportOverviewExcel
